'use client';
import { useEffect, useRef, useState } from 'react';
import { useClickOutside, useScrollPosition } from '@overlapdev/hooks';
import { ArrowDownSVG, ArrowUpSVG } from 'components/svg';

type DataType = {
	links: {
		anchor: string;
		sub_links?: { anchor: string; text: string }[];
		sub_menu: boolean;
		text: string;
	}[];
};

const AnchorMenu = ({ data }: { data: DataType }) => {
	//__Variables
	const { links } = data || {};

	//__Refs
	const ref = useRef<any>(null);

	//__Hooks
	const scrollPosition = useScrollPosition();
	const clickOutside = useClickOutside(ref);

	//__State
	const [current, setCurrent] = useState('');
	const [currentName, setCurrentName] = useState('');
	const [clicked, setClicked] = useState(false);
	const [open, setOpen] = useState(false);
	const [subOpen, setSubOpen] = useState(-1);

	//__Functions
	const anchorTo = (anchor: string) => {
		const block = document.getElementById(anchor);
		const header_height = 94;
		if (block) {
			setClicked(true);
			window.scrollTo({
				top: block.getBoundingClientRect().top - ref.current.clientHeight - header_height + window.pageYOffset,
				behavior: 'smooth'
			});
			setCurrent(anchor);
		}
		setOpen(false);
		setSubOpen(-1);
	};

	//__Use Effect
	useEffect(() => {
		if (!clicked) {
			links.map((link) => {
				const { anchor, sub_links, sub_menu } = link || {};
				const block = document.getElementById(anchor);
				if (block && block.getBoundingClientRect().top <= window.innerHeight / 2 && block.getBoundingClientRect().bottom > ref.current.clientHeight) {
					if (anchor) {
						setCurrent(anchor);
					}
				}

				if (sub_links?.length && sub_menu) {
					sub_links.map((link) => {
						const { anchor } = link || {};
						const block = document.getElementById(anchor);
						if (block && block.getBoundingClientRect().top <= window.innerHeight / 2 && block.getBoundingClientRect().bottom > ref.current.clientHeight) {
							if (anchor) {
								setCurrent(anchor);
							}
						}
					});
				}
			});
		} else {
			setTimeout(() => {
				setClicked(false);
			}, 600);
		}
	}, [scrollPosition]);

	useEffect(() => {
		links.map((link) => {
			const { anchor, sub_links, sub_menu } = link || {};
			if (anchor === current) {
				setCurrentName(link.text);
			}
			if (sub_links?.length && sub_menu) {
				sub_links.map((link) => {
					const { anchor } = link || {};
					if (anchor === current) {
						setCurrentName(link.text);
					}
				});
			}
		});
	}, [current]);

	useEffect(() => {
		if (clickOutside) {
			setOpen(false);
			setSubOpen(-1);
		}
	}, [clickOutside]);

	if (!links?.length) return null;
	return (
		<section className='font-semibold sticky top-[94px] z-10' ref={ref} id='anchor_menu'>
			<div className='bg-white border-y border-black/10 flex gap-8 justify-center py-7 max-md:hidden'>
				{links.map((item, index) => {
					const { anchor, sub_links, sub_menu, text } = item || {};
					return (
						<div key={`anchor_menu_${index}`} className='flex gap-3 items-center relative'>
							<a className={`cursor-pointer hover:text-dark-blue ${current === anchor ? 'text-primary' : ''}`} onClick={() => anchorTo(anchor)}>
								{text}
							</a>
							{sub_links?.length && sub_menu ? (
								<>
									<div
										className='cursor-pointer'
										onClick={() => {
											if (subOpen === index) {
												setSubOpen(-1);
											} else setSubOpen(index);
										}}
									>
										{subOpen === index ? <ArrowUpSVG width={14} /> : <ArrowDownSVG width={14} />}
									</div>
									<div className={`absolute bg-white flex flex-col gap-2 min-w-55 mt-2 overflow-hidden px-5 right-0 rounded-3xl shadow-[0px_20px_30px_0px_rgba(0,0,0,0.15)] top-full transition-ease w-full ${subOpen === index ? 'max-h-500 py-5' : 'max-h-0'}`}>
										{sub_links.map((item, index) => {
											const { anchor, text } = item || {};
											return (
												<a key={`anchor_menu_sub_${index}`} className={`cursor-pointer hover:text-dark-blue ${current === anchor ? 'text-primary' : ''}`} onClick={() => anchorTo(anchor)}>
													{text}
												</a>
											);
										})}
									</div>
								</>
							) : null}
						</div>
					);
				})}
			</div>
			<div className='pt-3 mx-5 md:hidden'>
				<div className='bg-white border border-light-grey px-5 py-2.5 relative rounded-full text-center' onClick={() => setOpen(!open)}>
					<span>{currentName || links[0].text}</span>
					<ArrowDownSVG className='absolute right-5 top-1/2 -translate-y-1/2' width={14} />
				</div>
				<div className='relative'>
					<div className={`absolute bg-white flex flex-col gap-4 left-0 overflow-hidden px-6 rounded-3xl shadow-[0px_20px_30px_0px_rgba(0,0,0,0.15)] top-0 transition-ease w-full ${open ? 'max-h-500 py-6' : 'max-h-0'}`}>
						{links.map((item, index) => {
							const { anchor, sub_links, sub_menu, text } = item || {};
							return (
								<div key={`anchor_menu_${index}`}>
									<a className={`cursor-pointer ${current === anchor ? 'text-primary' : ''}`} onClick={() => anchorTo(anchor)}>
										{text}
									</a>
									{sub_links?.length && sub_menu ? (
										<div className='flex flex-col gap-3 mt-3'>
											{sub_links.map((item, index) => {
												const { anchor, text } = item || {};
												return (
													<a key={`anchor_menu_sub_${index}`} className={`cursor-pointer pl-8 relative before:content-['—'] before:absolute before:left-0 before:top-0 ${current === anchor ? 'text-primary' : ''}`} onClick={() => anchorTo(anchor)}>
														{text}
													</a>
												);
											})}
										</div>
									) : null}
								</div>
							);
						})}
					</div>
				</div>
			</div>
		</section>
	);
};

export default AnchorMenu;
